"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import FacilityRequestModal from '@/components/common/FacilityRequestModal';
import ClinicianApplicationModal from '@/components/common/ClinicianApplicationModal';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [clinicianModalOpen, setClinicianModalOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'nav_home', label: 'Home', href: '/homepage' },
    { id: 'nav_facilities', label: 'Facilities', href: '/for-facilities' },
    { id: 'nav_clinicians', label: 'Clinicians', href: '/for-clinicians' },
    { id: 'nav_jobs', label: 'Jobs', href: '/jobs' },
    { id: 'nav_about', label: 'About', href: '/about' },
    { id: 'nav_contact', label: 'Contact', href: '/contact' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          scrolled 
            ? 'bg-[#f8faf9]/95 backdrop-blur-xl shadow-lg py-3 border-b border-[#E2E8F0]' 
            : 'bg-white/90 backdrop-blur-md py-5 border-b border-[#E2E8F0]/50'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo with Pill */}
            <div className="flex items-center gap-4">
              <Link href="/homepage" className="flex items-center transition-transform hover:scale-105 focus:outline-none">
                <Image
                  src="/assets/images/logo_for_site-1770531694569.png"
                  alt="INF Medical Logo"
                  width={200}
                  height={64}
                  className={`w-auto transition-all duration-500 ${scrolled ? 'h-12' : 'h-16'}`}
                  priority
                />
              </Link>
              <div className="hidden md:flex items-center px-3 py-1.5 rounded-full bg-[#f8faf9] border border-[#3a4d46]/30 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 bg-[#3a4d46] rounded-full mr-2 animate-pulse"></span>
                <span className="text-xs font-medium text-[#26342f] whitespace-nowrap">Permanent placements only</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks?.map((link) => (
                <Link
                  key={link?.id}
                  href={link?.href}
                  className={`text-base font-medium transition-all duration-300 relative group ${
                    pathname === link?.href
                      ? 'text-[#26342f]' : 'text-[#3a4d46] hover:text-[#26342f]'
                  }`}
                >
                  {link?.label}
                  <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-[#26342f] to-[#3a4d46] transform origin-left transition-transform duration-300 ${
                    pathname === link?.href ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}></span>
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => setFacilityModalOpen(true)}
                className="group relative px-6 py-2.5 text-base font-semibold text-white bg-gradient-to-r from-[#26342f] to-[#3a4d46] rounded-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#3a4d46] to-[#4d6359] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative flex items-center gap-2">
                  Request candidates
                  <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </button>
              <button
                onClick={() => setClinicianModalOpen(true)}
                className="px-6 py-2.5 text-base font-semibold text-[#26342f] border-2 border-[#3a4d46]/50 rounded-lg hover:bg-[#f8faf9] hover:border-[#26342f] transition-all duration-300"
              >
                Apply for jobs
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-[#3a4d46] hover:text-[#26342f] hover:bg-[#f8faf9] rounded-lg transition-all"
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-[#E2E8F0] pt-4 animate-fade-in-up">
              <div className="flex flex-col space-y-3">
                {navLinks?.map((link) => (
                  <Link
                    key={link?.id}
                    href={link?.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-base font-medium text-center transition-all px-4 py-2.5 rounded-lg ${
                      pathname === link?.href 
                        ? 'text-[#26342f] bg-[#f8faf9] border border-[#3a4d46]/30' 
                        : 'text-[#3a4d46] hover:bg-[#f8faf9] hover:text-[#26342f]'
                    }`}
                  >
                    {link?.label}
                  </Link>
                ))}
                <div className="flex flex-col gap-3 mt-6">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setFacilityModalOpen(true);
                    }}
                    className="px-5 py-3 text-base font-semibold text-center text-white bg-gradient-to-r from-[#26342f] to-[#3a4d46] rounded-lg shadow-lg flex items-center justify-center gap-2"
                  >
                    Request candidates
                    <Icon name="ArrowRightIcon" size={20} />
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setClinicianModalOpen(true);
                    }}
                    className="px-5 py-3 text-base font-semibold text-center text-[#26342f] border-2 border-[#3a4d46]/50 rounded-lg"
                  >
                    Apply for jobs
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Modals */}
      <FacilityRequestModal isOpen={facilityModalOpen} onClose={() => setFacilityModalOpen(false)} />
      <ClinicianApplicationModal isOpen={clinicianModalOpen} onClose={() => setClinicianModalOpen(false)} />
    </>
  );
}